import { t as trapFocus, r as removeTrapFocus } from './index-BIftRuJC.js';

/**
 * CartNotification
 */
class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.validContent = this.querySelector('[data-cart-notification-valid]');
    this.invalidContent = this.querySelector('[data-cart-notification-invalid]');
    this.modalTarget = this.querySelector('[data-modal-target]');
    this.dontShowCheckbox = this.querySelector('[data-cart-notification-dont-show]');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.bodyClass = 'cart-notification-is-open';

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    if (this.dontShowCheckbox) {
      this.dontShowCheckbox.addEventListener('change', this.handleDontShowChange.bind(this));
    }
  }

  get storageKey() {
    return 'cart-notification-disabled';
  }

  get isDisabled() {
    return localStorage.getItem(this.storageKey) === 'true';
  }

  handleDontShowChange(event) {
    const isChecked = event.target.checked;
    if (isChecked) {
      localStorage.setItem(this.storageKey, 'true');
    } else {
      localStorage.removeItem(this.storageKey);
    }
  }

  /**
   * open
   * @param {Boolean} hasError
   */
  open(hasError = false) {
    if (!hasError && this.isDisabled) {
      return;
    }

    if (hasError) {
      this.validContent.classList.add('hidden');
      this.invalidContent.classList.remove('hidden');
    } else {
      this.validContent.classList.remove('hidden');
      this.invalidContent.classList.add('hidden');
    }

    this.modalTarget.classList.remove('hidden');
    document.body.classList.add(this.bodyClass);

    this.modalTarget.addEventListener(
      'transitionend',
      () => {
        const notification = this.querySelector('[data-cart-notification]');
        notification.focus();
        trapFocus({ container: notification });
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  /**
   * close
   */
  close() {
    this.modalTarget.classList.add('hidden');
    document.body.classList.remove(this.bodyClass);
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }

  /**
   * renderContents
   * @param {Object} parsedState
   */
  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );
    });
    this.open();
  }

  /**
   * renderCartError
   * @param {String} description
   */
  renderCartError(description) {
    const errorDescriptionElem = this.querySelector('[data-cart-notification-error-description]');
    errorDescriptionElem.innerText = description;

    this.open(true);
  }

  /**
   * getSectionsToRender
   * @returns {Array} Sections to render
   */
  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        section: 'cart-notification-product',
        selector: `#cart-notification-product-${this.productId}`,
      },
      {
        id: 'cart-free-shipping-info',
        section: 'cart-free-shipping-info',
      },
      {
        id: 'cart-notification-button',
        section: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '[data-cart-icon-bubble]',
      },
    ];
  }

  /**
   * getSectionInnerHTML
   * @param {String} String that contains html
   * @param {String} selector
   * @returns
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return (
      new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || ''
    );
  }

  /**
   * handleBodyClick
   * @param {Object} event
   */
  handleBodyClick(event) {
    const target = event.target;
    const modalContent = this.querySelector('[data-cart-notification]');
    const isCloseButtonInThisModal = target.closest('[data-modal-close]') && this.contains(target);
    const isClickInOtherModal = target.closest('modal-window') && !this.contains(target);

    if (isClickInOtherModal) {
      return;
    }

    if (isCloseButtonInThisModal || !modalContent.contains(target)) {
      this.close();
    }
  }

  /**
   * setActiveElement
   * @param {Node} element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}
